import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { getEnrollmentForTrial, type EnrollmentDTO } from '@/lib/enrollments';
import { masterdataApi } from '@/lib/courses';
import type { TrialLessonDTO } from '@/lib/trials';
import { computeTeacherHonorarPerLessonCents, type TeacherArtRatesPerson } from '@/lib/migrationOfferHonorar';

export function useTrialOfferHonorarMissing(
  trial: TrialLessonDTO | null,
  enabled: boolean,
): { loading: boolean; missing: boolean; message: string; artName: string } {
  const [loading, setLoading] = useState(false);
  const [missing, setMissing] = useState(false);
  const [artName, setArtName] = useState('');

  const teacherWpUserId = useMemo(
    () => Number.parseInt(String(trial?.teacherId ?? ''), 10) || 0,
    [trial?.teacherId],
  );
  const teacherName = String(trial?.teacherName ?? 'Lehrer').trim() || 'Lehrer';

  useEffect(() => {
    if (!enabled || !trial || teacherWpUserId <= 0) {
      setLoading(false);
      setMissing(enabled && teacherWpUserId <= 0);
      setArtName('');
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const enrollment: EnrollmentDTO | null = await getEnrollmentForTrial(trial.id).catch(() => null);
        if (cancelled) return;
        const unterrichtsformId = enrollment?.unterrichtsformId ?? 0;
        const durationMin = enrollment?.durationMin ?? 0;
        if (unterrichtsformId <= 0 || durationMin <= 0) {
          setMissing(true);
          setArtName('');
          return;
        }
        const [person, forms] = await Promise.all([
          api.get<TeacherArtRatesPerson>(`/api/personen/${teacherWpUserId}`).catch(() => null),
          masterdataApi.unterrichtsformen().catch(() => []),
        ]);
        if (cancelled) return;
        const form = (forms || []).find((f) => f.id === unterrichtsformId);
        const artId = Number((form as { art_id?: number })?.art_id ?? 0);
        const resolvedArtName = String((form as { art_name?: string; name?: string })?.art_name
          ?? (form as { name?: string })?.name ?? 'Unterrichtsform-Art').trim();
        setArtName(resolvedArtName);
        const honorar = computeTeacherHonorarPerLessonCents(person, artId, durationMin);
        setMissing(honorar === null || honorar <= 0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [enabled, trial, teacherWpUserId]);

  const message = missing
    ? `Angebot kann nicht gesendet werden: Bei ${teacherName} ist kein Honorar${artName ? ` für ${artName}` : ''} hinterlegt. Bitte im Lehrerprofil unter Honorare pflegen.`
    : '';

  return { loading, missing, message, artName };
}
